import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BqxApplicationAbstractService } from 'bqx-application';
import { BqxAuthClientAbstractService, IAuthClientCredentials } from 'bqx-auth';
import { LoggerAbstractService, LogLevel } from 'bqx-logging';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, first, switchMap, takeUntil } from 'rxjs/operators';
import { AppConfig } from 'src/app/app.config';
import { AbstractAuthService } from './auth.abstract.service';
import { authAccessToken } from './auth.service';
interface PingUser {
    lanId: string | null;
    fullName: string | null;
    roles: string[];
}

@Injectable({
    providedIn: 'root'
})
export class PingAuthService implements AbstractAuthService {
    private _user: PingUser = { lanId: null, fullName: null, roles: [] };
    private _isAuthenticated = false;
    private _isLoggedOut = false;
    private _accessToken: string;
    private _authClient: IAuthClientCredentials;
    private unsubscribeSubject$: Subject<void> = new Subject<void>();
    private _sessionInvervalId;
    private _isBpLoggedIn$ = new BehaviorSubject<boolean>(false);

    get isBpLoggedIn$(): BehaviorSubject<boolean> {
        return this._isBpLoggedIn$;
    }

    get currentUser(): string {
        return this._user?.fullName ?? '';
    }

    get currentUserLanId(): string {
        return this._user?.lanId ?? null;
    }

    get currentUserRoles(): string {
        return this._user?.roles?.join(',') ?? '';
    }
    get accessToken(): string {
        return this._accessToken;
    }
    get isAuthenticated(): boolean {
        return this._isAuthenticated;
    }
    get isLoggedOut(): boolean {
        return this._isLoggedOut;
    }

    get shouldHaveSearchAccess(): boolean {
        return this._user.roles.some((role) =>
            role === 'bqxbase-01' ||
            role === 'bqxservicemanagement-01' ||
            role === 'bqxsupervisor-01' ||
            role === 'bqxspecialist-01' ||
            role === 'bqxadmin-01'
        );
    }

    constructor(
        private authService: BqxAuthClientAbstractService,
        private bqxApplication: BqxApplicationAbstractService,
        private http: HttpClient,
        private loggerService: LoggerAbstractService
    ) {
        this._authClient = this.authService.authClient;
    }

    public authenticateUser(setInterval = true): Observable<boolean> {
        return this._authClient.credentials$.pipe(
            takeUntil(this.unsubscribeSubject$),
            first((credentials) => !!credentials),
            switchMap((x) => {
                this._isAuthenticated = true;
                this._isLoggedOut = false;
                this._accessToken = x.accessToken;
                // lowercased once here so every role comparison downstream can be a plain match
                this._user.roles = (x.roles ?? []).map((role: string) => role?.toLowerCase());
                authAccessToken.next(this._accessToken);
                this._user.lanId = x.user ?? this.getLanIdFromClientData();
                this.callPingDirectory();
                sessionStorage.removeItem('session_expired');
                if (setInterval) this.sessionTimedOut();
                if (localStorage.getItem('bp-sdk-token')) {
                    this.bqxApplication.connect({ applicationType: 'Agent' }).subscribe((val) => {
                        this._isBpLoggedIn$.next(val);
                        return of(val);
                    });
                    return of(true);
                } else {
                    return this.bqxApplication.connect({ applicationType: 'Agent' });
                }
            }),
            catchError((err) => {
                this._isAuthenticated = false;
                this._isLoggedOut = true;
                console.log('Un-Authorized User: Redirecting to about:blank');
                window.location.href = 'about:blank';
                return of(false);
            })
        );
    }

    // 'client-data' is only written on a TokenReceived event, so it is absent when credentials come from the cookie
    private getLanIdFromClientData(): string | null {
        const clientData = localStorage.getItem('client-data');
        return clientData ? JSON.parse(clientData).sub : null;
    }

    // only sources the display name; roles come from the token via bqx-auth
    private callPingDirectory(): void {
        const lanId = this._user.lanId ?? this.getLanIdFromClientData();
        if (!lanId) {
            this.loggerService.postLogData(LogLevel.Error, 'Ping Directory', JSON.stringify({ success: false, error: 'No LAN ID available' }));
            return;
        }

        const directoryUri = AppConfig.settings.uiAuth.directoryUrl;
        const url = `${directoryUri}?AID=${encodeURIComponent(lanId)}`;
        this.http.get<{ FullName: string[] }>(url, {
            headers: { Authorization: `Bearer ${this._accessToken}` }
        }).pipe(
            takeUntil(this.unsubscribeSubject$)
        ).subscribe({
            next: ({ FullName }) => {
                this._user = { ...this._user, fullName: FullName?.[0] ?? null };
            },
            error: (err) => {
                this.loggerService.postLogData(LogLevel.Error, 'Ping Directory', JSON.stringify({ success: false, error: err }));
            }
        });
    }

    private bqxLogoutorRefresh(): void {
        this.bqxApplication.disConnect().subscribe((val) => {
            if (!val) {
                this.isBpLoggedIn$.next(val);
                localStorage.removeItem('bp-sdk-token_id');
                sessionStorage.removeItem('token_id');
                sessionStorage.removeItem('app-uid-token');
            }
        });
    }

    public logoutorRefresh(isExpired = false): void {
        this._isAuthenticated = false;
        this._isLoggedOut = true;
        this.unsubscribeSubject$.next(null);
        this.unsubscribeSubject$.complete();
        sessionStorage.clear();
        this.bqxLogoutorRefresh();
        clearInterval(this._sessionInvervalId);
        this._sessionInvervalId = null;
        this._authClient.refreshCredentials();
        if (isExpired) sessionStorage.setItem('session_expired', 'Y');
        else sessionStorage.setItem('session_expired', 'F');
    }

    private sessionTimedOut(): void {
        this._sessionInvervalId = setInterval(() => {
            if (!this._isLoggedOut)
                if (this._authClient.isCookieCredentialsExpired) {
                    this.logoutorRefresh(true);
                }
        }, 10000);
    }
}
